import { Injectable, inject } from '@angular/core';
import { CONFIG } from '@nano/core/config';
import { ErrorService } from '@nano/core/error';
import { Verification, VerificationStatusService } from '@nano/verification-status';
import { Observable, Subject, combineLatest, iif, merge, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, shareReplay, switchMap, startWith } from 'rxjs/operators';
import { RenameAccountMode } from '@nano/settings/accounts-settings/rename-account';
import { AuthService } from '@nano/core/auth';
import { AccountService } from '@nano/account/common';
import { OperationService } from '@nano/common/operation';
import { ApiEndpoints, ApiService, ApiArrayResponse } from '@nano/core/api';
import { HttpParams } from '@angular/common/http';
import { PaymentEntry } from '@nano/payment-entry';
import { SseService } from '@nano/core/sse';

@Injectable({ providedIn: 'root' })
export class NavService {
  readonly cardMenuVisible$: Observable<boolean>;
  readonly cardsEnabled$: Observable<boolean>;
  readonly ebanksisLoansEnabled$: Observable<boolean>;
  readonly userVerified$: Observable<boolean>;
  readonly loansEnabled$: Observable<boolean>;
  readonly privatePerson$: Observable<boolean>;
  readonly verificationInfoVisible$: Observable<boolean>;
  readonly pendingPaymentsVisible$: Observable<boolean>;
  readonly pendingOperationsAvailable$: Observable<boolean>;
  readonly pendingOperationsCount$: Observable<number>;
  readonly enduserApiVisible$: Observable<boolean>;
  readonly hasAccount$: Observable<boolean>;
  readonly renameAccountSettingsVisible$: Observable<boolean>;
  readonly userHasPartyAndRealAccountAndNotFailed$: Observable<boolean>;

  private config = inject(CONFIG);
  private pendingOperationsRefresh$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private accountService: AccountService,
    private verificationStatusService: VerificationStatusService,
    private errorService: ErrorService,
    private operationService: OperationService,
    private apiService: ApiService,
    private sseService: SseService
  ) {
    const selectedPartyHasAccounts$ = this.accountService.selectedPartyHasAccounts$.pipe(
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const userProfile$ = this.authService.userProfile$.pipe(
      filter((userProfile) => !!userProfile),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.hasAccount$ = combineLatest([this.accountService.hasAccount$, selectedPartyHasAccounts$]).pipe(
      map(([hasAccountFromUI, hasAccountFromService]) => hasAccountFromUI || hasAccountFromService)
    );

    const verification$ = combineLatest([userProfile$, this.accountService.selectedPartyHasAccounts$]).pipe(
      debounceTime(300),
      switchMap(() =>
        this.verificationStatusService.refreshVerification().pipe(
          catchError((error: unknown) => {
            this.errorService.handleRequestError(error);
            return of({ failCritical: false } as Verification);
          })
        )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.renameAccountSettingsVisible$ = userProfile$.pipe(
      map((userProfile) => {
        if (!this.config.renameAccountSettingsEnabled) {
          return false;
        }

        if (!userProfile.privatePerson) {
          if (this.config.renameAccountMode === RenameAccountMode.GLOBAL) {
            return userProfile.contractAdministrator;
          }
        }

        return true;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    if (this.config.identificationEnabled) {
      this.userHasPartyAndRealAccountAndNotFailed$ = combineLatest([userProfile$, selectedPartyHasAccounts$, verification$]).pipe(
        map(([user, hasRealAccount, verification]) => !!user.ibUserPartyId && hasRealAccount && !verification.failCritical)
      );
    } else {
      this.userHasPartyAndRealAccountAndNotFailed$ = combineLatest([userProfile$, selectedPartyHasAccounts$]).pipe(
        map(([user, hasRealAccount]) => !!user.ibUserPartyId && hasRealAccount)
      );
    }

    this.privatePerson$ = userProfile$.pipe(map((userProfile) => userProfile.privatePerson));

    this.cardMenuVisible$ = this.privatePerson$.pipe(
      map((privatePerson) => (privatePerson && this.config.cardsPrivateEnabled) || (!privatePerson && this.config.cardsCorporateEnabled))
    );

    this.cardsEnabled$ = combineLatest([this.cardMenuVisible$, this.userHasPartyAndRealAccountAndNotFailed$, userProfile$]).pipe(
      map(([cardMenuVisible, userHasAccountAndNotFailed, userProfile]) => cardMenuVisible && userHasAccountAndNotFailed && !!userProfile?.canAccessCards)
    );

    this.userVerified$ = combineLatest([userProfile$, this.hasAccount$]).pipe(
      map(([userProfile, userHasAccount]) => {
        return (
          (!userProfile.privatePerson && userHasAccount) ||
          (userProfile.privatePerson && userHasAccount && (userProfile.complianceLevel !== undefined ? +userProfile.complianceLevel > 0 : true)) ||
          !this.config.identificationEnabled
        );
      })
    );

    this.loansEnabled$ = this.privatePerson$.pipe(
      map((privatePerson) => {
        return !privatePerson && this.config.loansEnabled;
      })
    );

    this.ebanksisLoansEnabled$ = this.privatePerson$.pipe(
      map(() => {
        return this.config.ebanksisLoansEnabled;
      })
    );

    this.verificationInfoVisible$ = this.authService.userProfile$.pipe(filter((userProfile) => !!userProfile)).pipe(
      map((userProfile) => {
        return this.config.identificationEnabled && userProfile.privatePerson && this.config.displayVerificationMenu;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.pendingPaymentsVisible$ = userProfile$.pipe(
      map((user) => (this.config.paymentSavePrivateEnabled && user.privatePerson) || (this.config.paymentSaveCorporateEnabled && !user.privatePerson))
    );

    this.pendingOperationsAvailable$ = combineLatest([this.accountService.pendingPaymentsAvailable$, of(this.config.pendingOperationsBadgeEnabled)]).pipe(
      map(([pendingPaymentsAvailable, badgeEnabled]) => pendingPaymentsAvailable && badgeEnabled),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const pendingOperationsReloadTrigger$ = merge(
      this.pendingOperationsRefresh$.pipe(startWith(undefined)),
      this.sseService.paymentEntryUpdated$,
      this.operationService.operationActionCompleted$
    );
    const pendingOperationsCountSource$ = combineLatest([this.pendingOperationsAvailable$, pendingOperationsReloadTrigger$]).pipe(
      switchMap(([available]) => (available ? this.fetchPendingOperationsCount() : of(0)))
    );
    this.pendingOperationsCount$ = pendingOperationsCountSource$.pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.enduserApiVisible$ = this.authService.userProfile$.pipe(
      switchMap((user) =>
        iif(
          () => !user?.contractAdministrator,
          of(false),
          of((this.config.enduserApiPrivateEnabled && user?.privatePerson) || (this.config.enduserApiCorporateEnabled && !user?.privatePerson))
        )
      )
    );
  }

  private fetchPendingOperationsCount(): Observable<number> {
    return this.apiService.get<ApiArrayResponse<PaymentEntry>>(ApiEndpoints.paymentOperations(), this.createPendingOperationsParams()).pipe(
      map((result) => result.count ?? 0),
      catchError(() => of(0))
    );
  }

  private createPendingOperationsParams(): HttpParams {
    return new HttpParams().append('skip', '0').append('limit', '1').append('view', 'CONFIRMABLE');
  }

  refreshPendingOperationsCount(): void {
    this.pendingOperationsRefresh$.next();
  }
}
